import React, { useCallback, useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './common/Catalyst/table';
import { Badge } from './common/Catalyst/badge';
import { Text } from './common/Catalyst/text';
import { Button } from './common/Catalyst/button';
import { bankService } from './clients/ApiClients';
import { NsfItemReturn } from './clients/rust-api-client';
import {
  formatCurrency,
  formatDate,
  getBankErrorParts,
} from './utils/bankHelpers';
import { describeCutoff } from './utils/nsfHelpers';
import LoadingSpinner from './LoadingSpinner';
import ShowAlert from './ShowAlert';
import NsfDecisionDialog, { NsfDecisionMode } from './NsfDecisionDialog';
import NsfImageDialog from './NsfImageDialog';

interface NsfPanelProps {
  connectionId: string;
}

type BadgeColor = 'lime' | 'red' | 'amber' | 'zinc' | 'sky' | 'purple';

const decisionColor = (d: string): BadgeColor => {
  switch (d) {
    case 'undecided':
      return 'amber';
    case 'pay_requested':
      return 'sky';
    case 'return_requested':
      return 'purple';
    case 'decided_at_bank':
      return 'zinc';
    case 'expired':
      return 'red';
    default:
      return 'zinc';
  }
};

const decisionLabel = (item: NsfItemReturn): string => {
  switch (item.decision) {
    case 'undecided':
      return item.can_decide ? 'Needs decision' : 'Past cutoff';
    case 'pay_requested':
      return 'Pay requested';
    case 'return_requested':
      return 'Return requested';
    case 'decided_at_bank':
      return item.bank_request_type
        ? `${item.bank_request_type} at bank`
        : 'Decided at bank';
    case 'expired':
      return 'Expired';
    default:
      return item.decision;
  }
};

/**
 * The NSF queue for one connection. The rows come from the morning pull;
 * the decisions are made here. The bank returns an undecided item by
 * default, so **pay is the only decision that moves money**, and both
 * are final through the API — hence the one-shot dialog with a typed
 * confirmation. The cutoff is 1pm Central on the expiry date; after it
 * the buttons go away and the row says to phone bank support.
 */
const NsfPanel: React.FC<NsfPanelProps> = ({ connectionId }) => {
  const [items, setItems] = useState<NsfItemReturn[]>([]);
  const [open, setOpen] = useState(0);
  const [syncedThrough, setSyncedThrough] = useState<string | null>(null);
  const [openOnly, setOpenOnly] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deciding, setDeciding] = useState<{
    item: NsfItemReturn;
    mode: NsfDecisionMode;
  } | null>(null);
  const [viewing, setViewing] = useState<NsfItemReturn | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const resp = await bankService.listConnectionNsf({
        connectionId,
        openOnly,
      });
      setItems(resp.data.items);
      setOpen(resp.data.open);
      setSyncedThrough(resp.data.synced_through ?? null);
    } catch (e) {
      const { body } = getBankErrorParts(e);
      setError(body || "Couldn't load NSF items.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [connectionId, openOnly]);

  useEffect(() => {
    void load();
  }, [load]);

  // Replace the decided row in place and recount, rather than reloading:
  // the dialog is still showing the outcome and a reload would reorder
  // the table under it.
  const handleDecided = (after: NsfItemReturn) => {
    setItems((current) => {
      const next = current.map((i) => (i.id === after.id ? after : i));
      setOpen(next.filter((i) => i.decision === 'undecided').length);
      return next;
    });
  };

  const deadlineCell = (i: NsfItemReturn) => {
    const c = describeCutoff(i.decision_cutoff_at);
    if (!c) return <span>{formatDate(i.expire_date)}</span>;
    const urgent = i.decision === 'undecided' && !c.passed;
    return (
      <div className="flex flex-col">
        <span className={urgent ? 'font-medium' : ''}>{c.central}</span>
        <Text
          className={
            i.decision === 'undecided' && c.passed
              ? 'text-xs font-semibold text-red-700 dark:text-red-300'
              : 'text-xs'
          }
        >
          {c.remaining}
          {c.local ? ` · ${c.local} your time` : ''}
        </Text>
      </div>
    );
  };

  return (
    <div className="mt-4">
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <Text className="text-sm">
          {open === 0
            ? 'Nothing awaiting a decision.'
            : `${open} ${open === 1 ? 'item needs' : 'items need'} a decision.`}
          {syncedThrough
            ? ` Read from the bank through ${formatDate(syncedThrough)}.`
            : ' Not yet read from the bank.'}
        </Text>
        <Button plain onClick={() => setOpenOnly((v) => !v)}>
          {openOnly ? 'Show all' : 'Show open only'}
        </Button>
      </div>

      {error && (
        <ShowAlert
          alertType="error"
          alertTitle="Couldn't load"
          alertMessage={error}
          isVisible={!!error}
          onClose={() => setError(null)}
          timeout={12000}
          isInline={true}
        />
      )}

      <div className="bg-stone-100 dark:bg-gray-600 rounded-lg shadow overflow-hidden">
        <div className="px-4 py-5 sm:p-6">
          {loading ? (
            <LoadingSpinner />
          ) : items.length === 0 ? (
            <Text>
              {openOnly
                ? 'No open NSF items on this connection.'
                : 'No NSF items on this connection.'}
            </Text>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeader>Presented</TableHeader>
                    <TableHeader>Account</TableHeader>
                    <TableHeader>Item</TableHeader>
                    <TableHeader className="text-right">Amount</TableHeader>
                    <TableHeader className="text-right">
                      Balance after
                    </TableHeader>
                    <TableHeader>Deadline</TableHeader>
                    <TableHeader>Status</TableHeader>
                    <TableHeader className="text-right">Actions</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((i) => {
                    const isAch = i.source === 'ACH';
                    const pastCutoff =
                      i.decision === 'undecided' && !i.can_decide;
                    return (
                      <TableRow key={i.id}>
                        <TableCell>{formatDate(i.nsf_date)}</TableCell>
                        <TableCell className="tabular-nums">
                          {i.account_number ?? '—'}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Badge color={isAch ? 'sky' : 'zinc'}>
                              {isAch ? 'ACH' : 'Check'}
                            </Badge>
                            <span>
                              {isAch
                                ? i.ach_company_name ||
                                  i.ach_individual_name ||
                                  'ACH debit'
                                : `#${i.check_number ?? '?'}`}
                            </span>
                          </div>
                          {isAch &&
                            i.ach_individual_name &&
                            i.ach_company_name && (
                              <Text className="text-xs">
                                {i.ach_individual_name}
                              </Text>
                            )}
                          {i.matched_check_id && (
                            <Text className="text-xs">
                              In the check register
                            </Text>
                          )}
                          {i.mock_source_url && (
                            <Badge color="purple">Mock</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {i.amount ? formatCurrency(Number(i.amount)) : '—'}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {i.balance_after
                            ? formatCurrency(Number(i.balance_after))
                            : '—'}
                        </TableCell>
                        <TableCell>{deadlineCell(i)}</TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <Badge
                              color={
                                pastCutoff ? 'red' : decisionColor(i.decision)
                              }
                            >
                              {decisionLabel(i)}
                            </Badge>
                            {i.bank_request_status && (
                              <Text className="text-xs">
                                Bank: {i.bank_request_status}
                              </Text>
                            )}
                            {i.decision === 'undecided' &&
                              i.bank_request_error_message && (
                                <Text className="text-xs text-red-700 dark:text-red-300">
                                  {i.bank_request_error_message}
                                </Text>
                              )}
                            {pastCutoff && (
                              <Text className="text-xs">
                                Only Columbia Bank support can decide this now.
                              </Text>
                            )}
                            {(i.decision === 'pay_requested' ||
                              i.decision === 'return_requested') &&
                              i.decided_at && (
                                <Text className="text-xs">
                                  Requested {formatDate(i.decided_at)}
                                </Text>
                              )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            {!isAch && i.has_images && (
                              <Button plain onClick={() => setViewing(i)}>
                                View check
                              </Button>
                            )}
                            {i.can_decide && (
                              <>
                                <Button
                                  color="sky"
                                  onClick={() =>
                                    setDeciding({ item: i, mode: 'pay' })
                                  }
                                >
                                  Pay
                                </Button>
                                <Button
                                  outline
                                  onClick={() =>
                                    setDeciding({ item: i, mode: 'return' })
                                  }
                                >
                                  Return
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>
      <Text className="mt-2 text-xs">
        An item nobody decides is returned by the bank at the deadline, 1pm
        Central on its expiry date. Paying is the only decision that moves
        money. Both are final here; changing one afterwards is a phone call to
        Columbia Bank support.
      </Text>

      <NsfDecisionDialog
        item={deciding?.item ?? null}
        mode={deciding?.mode ?? 'return'}
        connectionId={connectionId}
        onClose={() => setDeciding(null)}
        onDecided={handleDecided}
      />
      <NsfImageDialog
        item={viewing}
        connectionId={connectionId}
        onClose={() => setViewing(null)}
      />
    </div>
  );
};

export default NsfPanel;
